// Client layer (TS-2.5.03). Swimlane rows (team groups) x quarter columns
// (RoadmapBoard.columns - server-derived per decisions.md D-147 / D-147's own header: "the client
// renders what the RoadmapBoard model says, never derives quarters itself"). Rendered as an
// ARIA table built from <div role="row"/"columnheader"/"rowheader"/"cell"> rather than a real
// <table>, since a cell may hold zero, one or several chips and a native <table> cannot express
// that without <br>-separated text. Scrolls horizontally inside its own container only
// (.eg-board-scroll), never at the document level.
import { resolveSelectedStatusLabels, isChipDimmed } from "./lib/statusResolution";
import { ProjectChip } from "./ProjectChip";
import type { FilterChoice, ProjectView, RoadmapBoard as RoadmapBoardModel } from "../../api/generated/contracts";
import { strings } from "../../strings";

export interface RoadmapBoardProps {
  readonly board: RoadmapBoardModel;
  readonly statuses: readonly FilterChoice[];
  readonly selectedStatuses: readonly string[];
  readonly dimmedOpacity: number;
  readonly emptyCellPlaceholder: string;
  readonly projectsByKey: ReadonlyMap<string, ProjectView>;
}

export function RoadmapBoard({
  board,
  statuses,
  selectedStatuses,
  dimmedOpacity,
  emptyCellPlaceholder,
  projectsByKey,
}: RoadmapBoardProps) {
  const selectedLabels = resolveSelectedStatusLabels(selectedStatuses, statuses);

  if (board.swimlanes.length === 0) {
    return <p className="eg-board-empty-state">{strings.roadmap.emptyBoard}</p>;
  }

  const columnTemplate = `220px repeat(${board.columns.length}, minmax(200px, 1fr))`;

  return (
    <div className="eg-board-scroll">
      <div className="eg-board" role="table" aria-label={strings.roadmap.aria.board} style={{ gridTemplateColumns: columnTemplate }}>
        <div role="row" className="eg-board__row eg-board__row--header">
          <span role="columnheader" className="eg-board__lane-spacer" />
          {board.columns.map((column) => (
            <span role="columnheader" key={column.key} className="eg-board__column-header">
              {column.label}
            </span>
          ))}
        </div>
        {board.swimlanes.map((lane) => (
          <div role="row" key={lane.teamGroup} className="eg-board__row">
            <span role="rowheader" className="eg-board__lane-header">
              {lane.teamGroup}
            </span>
            {board.columns.map((column) => {
              const chips = lane.chips.filter((chip) => chip.columnKey === column.key);
              return (
                <span role="cell" key={column.key} className="eg-board__cell">
                  {chips.length === 0 ? (
                    <span className="eg-board__placeholder" aria-hidden="true">
                      {emptyCellPlaceholder}
                    </span>
                  ) : (
                    chips.map((chip) => (
                      <ProjectChip
                        key={chip.projectKey}
                        chip={chip}
                        dimmed={isChipDimmed(chip.statusCanonical, selectedLabels)}
                        dimmedOpacity={dimmedOpacity}
                        columnLabel={column.label}
                        teamGroupLabel={lane.teamGroup}
                        detail={projectsByKey.get(chip.projectKey)}
                      />
                    ))
                  )}
                </span>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
